import { API } from 'aws-amplify';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as mutations from 'graphql/mutations';
import { GameCarouselType, ScopeType } from './useGetGames';

interface PreviousGamesState {
  [key: string]: any;
}

export const useDeleteGameMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (variables: { missionID: string }) => {
      const response = await API.graphql({
        query: mutations.deleteGame,
        variables: {
          missionID: variables.missionID,
        },
      });
      return response;
    },
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey: ['games'] });

      const scopes = [ScopeType.all, ScopeType.circle, ScopeType.person];
      const types = [
        GameCarouselType.current,
        GameCarouselType.future,
        GameCarouselType.past,
      ];

      const queryKeys = scopes.flatMap((scope) =>
        types.map((type) => ['games', { type, scope }])
      );

      const previousGames: PreviousGamesState = {};
      queryKeys.forEach((key) => {
        const currentData = queryClient.getQueryData(key);
        if (currentData) {
          previousGames[JSON.stringify(key)] = currentData;
        }
      });

      queryKeys.forEach((key) => {
        queryClient.setQueryData(key, (old: any[] = []) => {
          if (!old) return [];
          return old.filter((game) => game.id !== variables.missionID);
        });
      });

      return { previousGames };
    },
    onError: (error: Error, variables, context) => {
      console.error('Failed to delete game:', error);
      if (context?.previousGames) {
        Object.entries(context.previousGames).forEach(([keyString, value]) => {
          const key = JSON.parse(keyString);
          queryClient.setQueryData(key, value);
        });
      }
    },
    onSuccess: () => {
      // Invalidate all games queries to ensure fresh data after successful deletion
      const scopes = [ScopeType.all, ScopeType.circle, ScopeType.person];
      const types = [
        GameCarouselType.current,
        GameCarouselType.future,
        GameCarouselType.past,
      ];

      scopes.forEach((scope) => {
        types.forEach((type) => {
          queryClient.invalidateQueries({
            queryKey: ['games', { type, scope }],
          });
        });
      });

      queryClient.invalidateQueries({ queryKey: ['games'] });
    },
  });
};
